import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import { getGallery } from '../../seo/galleryConfig';

const faqs = [
  {
    q: 'How quickly can an emergency plumber actually reach me?',
    a: 'For genuine emergencies — burst pipes, major leaks, no heat in winter, sewage backups — we aim for under 90 minutes across most of Kent, Greater London and Essex. The exact ETA depends on traffic and our nearest engineer\'s current job, but you will get a realistic time on the phone, not a vague promise.'
  },
  {
    q: 'What counts as a plumbing emergency?',
    a: 'Anything that is causing damage right now (water leaking through ceilings, flooding floors, sewage backflow), anything that has left you without an essential service (no heating mid-winter, no hot water in a household with elderly residents or young children), or anything that could become dangerous within hours (a gas-related water leak, a heavy drip near electrics). If you are unsure, call us — advice over the phone is free.'
  },
  {
    q: 'Do you charge more for out-of-hours emergency callouts?',
    a: 'There is a modest premium for evenings, nights and Sundays — but you will be told the figure on the phone before we dispatch, so there are no surprises. Often the cost of acting now is far lower than the damage caused by waiting.'
  },
  {
    q: 'How do I stop a burst pipe before you arrive?',
    a: 'Turn off your water at the internal stopcock (usually under the kitchen sink or in a downstairs cupboard). Turn off the central heating and open the cold taps to drain the system. If water is anywhere near electrics, turn the power off at the consumer unit. Stay on the line — we will guide you through it.'
  },
  {
    q: 'Are you really available 24/7, or just to certain postcodes?',
    a: 'Our phones are answered 24/7. Engineer availability across the South-East is very strong, but in remote rural pockets very late at night we may give you a realistic ETA that is slightly longer. We will never leave you without a clear answer or refer you elsewhere with no plan.'
  },
  {
    q: 'Will you bill my insurance company for the repair?',
    a: 'Yes — many emergency repairs are claimable on home or landlord insurance. We provide itemised invoices, photographic evidence of damage, and a written description of the works, which insurers typically accept without issue.'
  },
];

const relatedServices = [
  { slug: 'plumbing', label: 'Plumbing Services', blurb: 'Everyday plumbing repairs, installs and upgrades.' },
  { slug: 'property-maintenance', label: 'Property Maintenance', blurb: 'Stop emergencies before they start — planned maintenance plans.' },
  { slug: 'handyman', label: 'Handyman Services', blurb: 'After the leak is stopped, we can repair damaged fittings, doors and walls.' },
];

const EmergencyPlumberPage = () => (
  <ServicePageLayout
    seoTitle="Emergency Plumber Near Me | 24 Hour Plumbing Repairs | H.A.R.P Solutions LTD"
    seoDescription="24/7 emergency plumber covering Kent, London and Essex. Burst pipes, leaks, no heat, blocked toilets — fast response, transparent pricing. Call +44 330 122 1094 day or night."
    canonicalPath="/services/emergency-plumber"
    keywords="emergency plumber, 24 hour plumber, emergency plumbing repairs, urgent plumbing services, burst pipe, emergency plumber Kent, emergency plumber London, emergency plumber Essex"
    pageTitle="24/7 Emergency Plumber for Kent, London & Essex"
    serviceName="Emergency Plumber"
    serviceSlug="emergency-plumber"
    galleryImages={getGallery('emergency-plumber')}
    heroSubheading="Pick up the phone — we'll pick up too. Day or night."
    intro="When a pipe bursts at 3am or your boiler dies in the middle of January, you do not need clever marketing — you need an actual plumber to ring you back within minutes. H.A.R.P Solutions provides genuine 24/7 emergency plumbing across Kent, Greater London and Essex, with a real human on the phone every hour of every day."
    bullets={[
      'Real humans answering the phone 24/7',
      'Targeted under-90-minute response',
      'Burst pipes, leaks, no-heat and drainage',
      'Insurance-friendly written reports',
      'Transparent out-of-hours pricing',
      'Domestic, landlord and small commercial',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
  >
    <h2>Stop the Damage Before It Gets Worse</h2>
    <p>
      A genuine plumbing emergency is a race against time. Every minute a burst pipe is uncontained, water spreads further into floorboards,
      walls and ceilings — and every additional litre is a few hundred pounds of damage. Every hour your home is without heat in winter is
      another hour of discomfort, and for elderly residents or young children it can quickly turn into a health concern. Acting fast,
      with someone who knows what they are doing, is always cheaper and safer than waiting until morning.
    </p>
    <p>
      H.A.R.P Solutions runs a true 24-hour emergency plumbing service across the South-East. The phone is answered by a real person at any
      time of day or night, and our nearest available engineer is dispatched immediately. While they travel to you, we'll talk you through
      the steps to limit damage — finding the stopcock, isolating power, opening drain points — so the situation is already stabilised by the
      time they walk through the door.
    </p>

    <h2>When to Call Us Immediately</h2>
    <p>Call us straight away on <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> for any of the following:</p>
    <ul>
      <li><strong>Burst or leaking pipes</strong> — visible water under sinks, behind walls, or coming through a ceiling.</li>
      <li><strong>No heat or hot water</strong> — especially in cold weather, or in properties with vulnerable occupants.</li>
      <li><strong>Boiler leaks or pressure drops</strong> — dripping near the boiler or a pressure gauge that won't hold.</li>
      <li><strong>Blocked or overflowing toilets</strong> — particularly in single-bathroom homes or commercial premises.</li>
      <li><strong>Sewage backups</strong> — foul smells, gurgling drains, or waste backing up into showers and baths.</li>
      <li><strong>Leaking radiators</strong> — drips onto carpets or floors that you cannot temporarily isolate.</li>
      <li><strong>Failed stopcock</strong> — you cannot turn the mains off and water keeps flowing.</li>
      <li><strong>Tank or cylinder leaks</strong> — water dripping from loft tanks or hot-water cylinders.</li>
    </ul>

    <h2>What We Need From You On The Call</h2>
    <p>
      To dispatch the right engineer with the right parts, we will ask a few quick questions:
    </p>
    <ol>
      <li><strong>Your address and postcode</strong> so we can pick the closest available engineer.</li>
      <li><strong>What is happening right now</strong> in plain language — "water is coming through the bedroom ceiling" is more useful than "there's a leak".</li>
      <li><strong>Have you turned the water off?</strong> If not, we'll guide you through it.</li>
      <li><strong>Is anyone vulnerable on the property</strong> — elderly residents, infants, anyone with mobility issues — so we can prioritise.</li>
      <li><strong>Access details</strong> — is parking available, are keys with a neighbour, is the property tenanted?</li>
    </ol>

    <h2>Pricing for Emergency Work</h2>
    <p>
      Emergency callouts cost more than scheduled work — that is true of every reputable firm — but we keep it transparent. You will hear the
      hourly rate, the typical job duration, and any parts surcharge <em>before</em> we dispatch. If the job turns out to be larger than first
      thought, we stop, call you, and agree the new figure before continuing. There are no hidden "out-of-hours fees" added to your final
      invoice.
    </p>
    <p>
      For tenants and landlords, we provide written reports and photographic evidence suitable for insurance claims and dilapidations
      disputes. Many emergency callouts are recoverable on a buildings or landlord insurance policy.
    </p>

    <h2>Areas Covered 24/7</h2>
    <p>
      Our emergency engineers are based across the region so wherever you are in our coverage area, someone is usually close by:
    </p>
    <ul>
      <li><strong>Kent:</strong> Rochester, Gillingham, Chatham, Maidstone, Dartford, Gravesend, Sittingbourne, Tonbridge, Sevenoaks.</li>
      <li><strong>Greater London:</strong> all 32 boroughs, with strongest 24-hour coverage in East, South-East and Central London.</li>
      <li><strong>Essex:</strong> Romford, Brentwood, Basildon, Chelmsford, Southend-on-Sea and surrounding commuter belt.</li>
    </ul>

    <h2>Already Stopped The Leak? You Still Need Us.</h2>
    <p>
      Many people manage to turn their water off and stop the immediate flood, but the underlying cause still needs proper repair before you
      can safely turn the water back on. We are happy to attend "next-morning" rather than truly out-of-hours if you have contained the
      situation — just say so on the call and you will pay our standard daytime rate.
    </p>

    <h2>Call Now</h2>
    <p>
      <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> — 24 hours, every day. If you'd prefer to message rather than speak,
      the WhatsApp button at the bottom of every page goes to the same number. For non-urgent plumbing, our{' '}
      <a href="/services/plumbing">standard plumbing services page</a> covers what to expect.
    </p>
  </ServicePageLayout>
);

export default EmergencyPlumberPage;
